import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { Rights } from 'src/app/models/enums/rights';
import { Group } from 'src/app/models/group';
import { Role } from 'src/app/models/role';
import { GroupsService } from 'src/app/services/groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { RolesService } from 'src/app/services/roles.service';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { IGroup } from '../../../models/interfaces/group.interface';
import { GroupFormContext } from '../group-form-context';

@Component({
    selector: 'app-group-form',
    templateUrl: './group-form.component.html',
    styleUrls: ['./group-form.component.scss'],
    imports: [FormsModule, FormFocusDirective, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, MatButtonModule, NgClass]
})
export class GroupFormComponent extends ManagementFormComponent<IGroup> implements OnInit, AfterViewInit, OnChanges {
    public faSave = faSave;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du group à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('group')
    public dataInput: Required<IGroup> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: GroupFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<GroupFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: GroupFormContext) {
        this._context$.next(value);
    }

    /**
     * Liste des groupes proposés dans la liste déroulante Groupe parent du formulaire.
     */
    public groupParents: Group[];

    /**
     * Liste des rôles proposés dans la liste déroulante rôle du formulaire.
     */
    public roles: Role[];

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    public isOrganizationRoot: boolean;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('groupUpdated')
    public dataOutput: EventEmitter<IGroup> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    public userRolesModifyRight: boolean = false;

    public formValuesChanges: boolean = false;

    @ViewChild('roleselect') roleselect: MatSelect;

    /**
     * Constructeur
     * @param groupsService Instance du service des clients.
     */
    constructor(
        protected memoryService: MemoryService,
        private groupsService: GroupsService,
        private rolesService: RolesService
    ) {
        super();

        this.userRolesModifyRight = memoryService.getConnectedUser().hasRight(Rights.ModifyUserRoles);
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
            this.isOrganizationRoot = x.item.isOrganizationRoot;

            // if (this.isOrganizationRoot || this._context?.mode == FormMode.Reading) {
            //   this.formGroup.get('name').disable();
            // } else {
            //   this.formGroup.get('name').enable();
            // }

            this.groupsService.List(null, null, null, null).subscribe((groups) => {
                this.groupParents = groups.value.filter((group) => group.id !== x.item.id);
            });

            this.rolesService.ListAttributable().subscribe((roles) => {
                // Tous les rôles sauf SuperAdmin
                this.roles = roles.value.filter((x) => x.id !== 0);
            });
        });

        this.formGroup.valueChanges.subscribe((x) => {
            this.formValuesChanges = true;
        });
    }

    ngAfterViewInit() {
        this.roleselect.openedChange.subscribe((opened) => {
            if (opened) {
                this.roleselect.panel.nativeElement.addEventListener('mouseleave', () => {
                    this.roleselect.close();
                });
            }
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
        this.formGroup.get('name').disable();
        this.formGroup.get('parentGroupId').disable();

        if (this.userRolesModifyRight === false) {
            this.formGroup.get('roles').disable();
        }
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IGroup> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null),
            parentGroupId: new UntypedFormControl(null),
            roles: new UntypedFormControl(null),
            isOrganizationRoot: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        if (this.formGroup.invalid || this.formGroup.disabled) {
            return;
        }

        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
